import React, { useMemo, useState } from 'react';
import { FmlxModal, FmlxWizard } from 'fmlx-common-ui';
import ImportSynchTripResultContext from './context';
import { SynchTripResultImportDTO } from 'business/models/Models';
import SynchTripResultFileImport from './fileUpload';
import SynchTripResultVerfication from './verification';
import SynchTripResultImportData from './importData';

interface ImportSynchTripResultWizardProps {
  closeSynchTrip: () => void;
  refetchCollectedCrystals: () => void;
}

enum WizardSteps {
  FILE_UPLOAD = 0,
  DATA_VERIFICATION = 1,
  IMPORT_DATA = 2,
}

const ImportSynchTripResultWizard = (props: ImportSynchTripResultWizardProps) => {
  const { closeSynchTrip, refetchCollectedCrystals } = props;
  const [nextButtonDisallowed, setNextButtonDisallowed] = useState(true);
  const [fileContent, setFileContent] = useState('');
  const [synchTripResultImportDTOList, setSynchTripResultImportDTOList] =
    useState<SynchTripResultImportDTO[]>();

  const [activeStep, setActiveStep] = useState(WizardSteps.FILE_UPLOAD);
  const [openState, setOpenState] = useState(true);

  const steps = [
    {
      id: WizardSteps.FILE_UPLOAD,
      stepper: {
        title: 'Upload File',
        desc: 'Please Upload File',
        disabled: nextButtonDisallowed,
        error: false,
      },
      content: <SynchTripResultFileImport />,
      nextStepCallback: () => {
        setActiveStep((prevState) => prevState + 1);
      },
      backStepCallback: () => {
        // code to move to the previous step
      },
    },
    {
      id: WizardSteps.DATA_VERIFICATION,
      stepper: {
        title: 'Verification Data',
        desc: 'Please Verify Data',
        disabled: nextButtonDisallowed,
        error: false,
      },
      content: <SynchTripResultVerfication />,
      nextStepCallback: () => {
        setActiveStep((prevState) => prevState + 1);
      },
      backStepCallback: () => {
        setActiveStep((prevState) => prevState - 1);
      },
    },
    {
      id: WizardSteps.IMPORT_DATA,
      stepper: {
        title: 'Import Data',
        desc: 'Please create the experiment(s)',
        disabled: true,
        error: false,
      },
      content: (
        <SynchTripResultImportData refetchCollectedCrystals={refetchCollectedCrystals} />
      ),
      nextStepCallback: () => {
        setActiveStep((prevState) => prevState + 1);
      },
      backStepCallback: () => {
        setActiveStep((prevState) => prevState - 1);
      },
    },
  ];

  const nextButtonDisabled = useMemo(() => {
    switch (activeStep) {
      case WizardSteps.FILE_UPLOAD:
        return nextButtonDisallowed;
      case WizardSteps.DATA_VERIFICATION:
        return nextButtonDisallowed;
      case WizardSteps.IMPORT_DATA:
        return false;
    }
  }, [activeStep, nextButtonDisallowed]);

  const handleCloseClick = () => {
    closeSynchTrip();
    setOpenState(false);
  };

  const handleStepClick = (stepIndex) => {
    setActiveStep(stepIndex);
  };

  const handleCancelClick = () => {
    closeSynchTrip();
    setActiveStep(0);
  };

  const handleSkipClick = () => {
    if (activeStep < steps.length - 1) {
      setActiveStep((prevState) => prevState + 1);
    }
  };

  return (
    <ImportSynchTripResultContext.Provider
      value={{
        fileContent,
        setFileContent,
        setNextButtonDisallowed,
        synchTripResultImportDTOList,
        setSynchTripResultImportDTOList,
      }}
    >
      <FmlxWizard
        id="importsynchtripresult-wizard"
        title="Import Synchrotron Trip Result"
        subtitle="This wizard will help you import the synchrotron trip results."
        steps={steps}
        activeStep={activeStep}
        open={openState}
        onCloseClick={handleCloseClick}
        onStepClick={handleStepClick}
        onCancelClick={handleCancelClick}
        onSkipClick={handleSkipClick}
        onFinishClick={() => closeSynchTrip()}
        disableNextButton={nextButtonDisabled}
        type={FmlxWizard.Type.MODAL}
        modalSize={FmlxModal.Size.DOUBLE_EXTRA_LARGE}
      />
    </ImportSynchTripResultContext.Provider>
  );
};

export default ImportSynchTripResultWizard;
